import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";

export interface IngestionRunDetailsPayload {
  percent?: number;
  stage?: string;
  summary?: Record<string, unknown> | null;
}

export function ingestionStatusNote(status: string | null | undefined): string {
  const raw = String(status || "");
  const pipe = raw.indexOf("|");
  if (pipe < 0) return "";
  return raw.slice(pipe + 1).trim();
}

export function readIngestionDetails(ingestion: { details?: unknown; details_json?: unknown } | null | undefined): IngestionRunDetailsPayload | null {
  const raw = ingestion?.details ?? ingestion?.details_json;
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as IngestionRunDetailsPayload) : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as IngestionRunDetailsPayload;
  return null;
}

export function runningIngestionPercent(
  status: string | null | undefined,
  details?: IngestionRunDetailsPayload | null
): number | null {
  if (typeof details?.percent === "number" && Number.isFinite(details.percent)) {
    return Math.max(0, Math.min(100, Math.round(details.percent)));
  }
  const match = String(status || "").match(/RUNNING\s+(\d+)\s*%/i);
  if (!match) return null;
  return Math.max(0, Math.min(100, Number(match[1])));
}

export function formatIngestionStatusLabel(status: string | null | undefined): string {
  const raw = String(status || "").trim();
  if (!raw) return "Idle";
  const upper = raw.toUpperCase();
  if (upper.startsWith("RUNNING")) return "Running";
  if (upper === "SUCCESS" || upper.startsWith("SUCCESS|")) return "Success";
  return raw;
}

function summaryValue(summary: Record<string, unknown>, key: string): number {
  const value = summary[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function filteredDeletionValue(summary: Record<string, unknown>): string {
  const count = summaryValue(summary, "rows_deleted_filtered");
  const reasons = summary.filtered_deletion_reasons;
  if (!reasons || typeof reasons !== "object" || Array.isArray(reasons)) {
    return count === 0 ? `${count} (no filtered deletions)` : String(count);
  }
  const entries = Object.entries(reasons as Record<string, unknown>).filter(([, value]) => Number(value) > 0);
  if (!entries.length) return `${count} (no filtered deletions)`;
  return `${count} (${entries.map(([reason, value]) => `${reason}: ${value}`).join(", ")})`;
}

export function ingestionSummaryLines(
  summary: Record<string, unknown> | null | undefined
): Array<{ label: string; value: string }> {
  if (!summary || typeof summary !== "object") return [];
  if (summary.kind === "tickets") {
    return [
      { label: "Tickets fetched from PSA", value: String(summaryValue(summary, "tickets_fetched")) },
      { label: "Tickets in summarization work queue", value: String(summaryValue(summary, "tickets_processed")) },
      { label: "Skipped (unchanged PSA payload)", value: String(summaryValue(summary, "skipped_unchanged")) },
      { label: "Summary tasks completed", value: String(summaryValue(summary, "summarize_tasks_completed")) },
      { label: "MySQL-only tickets (not worth indexing)", value: String(summaryValue(summary, "skipped_not_worth_indexing")) },
      { label: "Tickets inserted/updated", value: String(summaryValue(summary, "tickets_inserted")) },
      { label: "Ticket vector facet upserts", value: String(summaryValue(summary, "vector_store_inserted")) },
    ];
  }
  const lines = [
    { label: "Source documents processed", value: String(summaryValue(summary, "source_documents")) },
    { label: "Documents skipped (unchanged)", value: String(summaryValue(summary, "skipped_unchanged")) },
    { label: "Documents processed (changed)", value: String(summaryValue(summary, "processed_changed")) },
    { label: "Total sections generated", value: String(summaryValue(summary, "sections_generated")) },
    { label: "Sections upserted", value: String(summaryValue(summary, "sections_upserted")) },
    { label: "Rows deleted (confirmed 404 at source)", value: String(summaryValue(summary, "rows_deleted_absent")) },
    { label: "Rows deleted (filtered at source)", value: filteredDeletionValue(summary) },
    { label: "Vertex vectors deleted", value: String(summaryValue(summary, "vectors_deleted")) },
    { label: "Documents retained after fetch failure", value: String(summaryValue(summary, "retained_after_fetch_failure")) },
    { label: "Deletion candidates that confirmed present", value: String(summaryValue(summary, "deletion_candidates_present")) },
    { label: "Deletion candidates with an unknown outcome", value: String(summaryValue(summary, "deletion_candidates_unknown")) },
  ];
  const suppressed = summary.deletions_suppressed_for;
  if (Array.isArray(suppressed) && suppressed.length) {
    lines.push({
      label: "Deletions suppressed after fetch failure",
      value: suppressed.map((item) => String(item)).join(", "),
    });
  }
  return lines;
}

interface IngestionRunDetailsProps {
  status: string | null | undefined;
  details?: IngestionRunDetailsPayload | null;
}

const IngestionRunDetails: React.FC<IngestionRunDetailsProps> = ({ status, details }) => {
  const running = String(status || "").toUpperCase().startsWith("RUNNING");
  const percent = runningIngestionPercent(status, details);
  const stage = String(details?.stage || ingestionStatusNote(status) || "").trim();
  const lines = ingestionSummaryLines(details?.summary || null);
  const statusSummary = !running && lines.length === 0 ? ingestionStatusNote(status) : "";

  if (!running && lines.length === 0 && !statusSummary) return null;

  return (
    <Box sx={{ mt: 1.25, width: "100%" }}>
      {running && (
        <Box sx={{ mb: lines.length ? 1.25 : 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
            <Typography sx={{ fontSize: "13px", color: "#374151" }}>
              {stage || "Ingestion in progress"}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#666F8D", flexShrink: 0 }}>
              {percent == null ? "" : `${percent}%`}
            </Typography>
          </Box>
          <LinearProgress
            variant={percent == null ? "indeterminate" : "determinate"}
            value={percent ?? 0}
            sx={{
              height: 8,
              borderRadius: "999px",
              backgroundColor: "#E7E1DA",
              "& .MuiLinearProgress-bar": { backgroundColor: "#705D4B", borderRadius: "999px" },
            }}
          />
        </Box>
      )}
      {statusSummary ? (
        <Box>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.04em", color: "#6B7280", mb: 0.5 }}>
            INGESTION SUMMARY
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#111827" }}>{statusSummary}</Typography>
        </Box>
      ) : null}
      {lines.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.04em", color: "#6B7280", mb: 0.5 }}>
            INGESTION SUMMARY
          </Typography>
          {lines.map((line) => (
            <Box
              key={line.label}
              sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.15 }}
            >
              <Typography sx={{ fontSize: "13px", color: "#4B5563" }}>{line.label}</Typography>
              <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 600, textAlign: "right" }}>
                {line.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default IngestionRunDetails;
